import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Modal, ModalContent } from '@hsl-fi/dialog';
import { createInstance } from 'i18next';
import { useState } from 'react';
import { TextInput } from '@hsl-fi/form';
import { Button } from '@hsl-fi/layout-primitives';
import cx from 'classnames';
import Icon from '@digitransit-component/digitransit-component-icon';
import { jsx, jsxs } from 'react/jsx-runtime';

/* eslint sort-keys: "error" */
const translations = {
  de: {
    translation: {
      cancel: 'Abbrechen',
      'choose-icon': 'Wählen Sie ein Symbol für diesen Ort',
      'edit-place': 'Ort bearbeiten',
      'input-placeholder': 'Einen Namen für Ort setzen (optional)',
      save: 'Speichern',
      'save-place': 'Favorit speichern'
    }
  },
  en: {
    translation: {
      cancel: 'Cancel',
      'cannot-save-place': 'Cannot save a place without address or icon',
      'choose-icon': "Select the place's icon",
      'edit-place': 'Edit place',
      home: 'home',
      'input-placeholder': 'Give the name a place (optional)',
      place: 'a place',
      'required-text': 'required field',
      save: 'Save',
      'save-place': 'Save place',
      school: 'school',
      shopping: 'shopping',
      sport: 'sport',
      work: 'work'
    }
  },
  fi: {
    translation: {
      cancel: 'Peruuta',
      'cannot-save-place': 'Paikkaa ilman osoitetta tai ikonia ei voida tallentaa',
      'choose-icon': 'Valitse paikan kuvake',
      'edit-place': 'Muokkaa paikkaa',
      home: 'koti',
      'input-placeholder': 'Anna paikalle nimi (vapaaehtoinen)',
      place: 'paikka',
      'required-text': 'pakollinen kenttä',
      save: 'Tallenna',
      'save-place': 'Tallenna paikka',
      school: 'koulu',
      shopping: 'ostokset',
      sport: 'urheilu',
      work: 'työ'
    }
  },
  pl: {
    translation: {
      cancel: 'Anuluj',
      'cannot-save-place': 'Nie można zapisać miejsca bez nazwy lub ikony',
      'choose-icon': 'Wybierz ikonę miejsca',
      'edit-place': 'Edytuj miejsce',
      home: 'dom',
      'input-placeholder': 'Nadaj nazwę miejscu (opcjonalne)',
      place: 'miejsce',
      'required-text': 'pole wymagane',
      save: 'Zapisz',
      'save-place': 'Zapisz miejsce',
      school: 'szkoła',
      shopping: 'zakupy',
      sport: 'sport',
      work: 'praca'
    }
  },
  sv: {
    translation: {
      cancel: 'Avbryt',
      'cannot-save-place': 'Det går inte att spara en plats utan adress eller ikon',
      'choose-icon': 'Välj platsens ikon',
      'edit-place': 'Redigera plats',
      home: 'hem',
      'input-placeholder': 'Ange platsens namn (frivilligt)',
      place: 'en plats',
      'required-text': 'obligatoriskt fält',
      save: 'Spara',
      'save-place': 'Spara plats',
      school: 'skola',
      shopping: 'shoppa',
      sport: 'sport',
      work: 'arbete'
    }
  }
};

const i18n = createInstance({
  fallbackLng: 'fi',
  // debug: true,
  resources: translations,
  defaultNS: 'translation',
  react: {
    useSuspense: true
  }
});
i18n.init();

function _objectWithoutPropertiesLoose(r, e) {
  if (null == r) return {};
  var t = {};
  for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
    if (-1 !== e.indexOf(n)) continue;
    t[n] = r[n];
  }
  return t;
}

function _objectWithoutProperties(e, t) {
  if (null == e) return {};
  var o,
    r,
    i = _objectWithoutPropertiesLoose(e, t);
  if (Object.getOwnPropertySymbols) {
    var n = Object.getOwnPropertySymbols(e);
    for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]);
  }
  return i;
}

function _typeof(o) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) {
    return typeof o;
  } : function (o) {
    return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
  }, _typeof(o);
}

function toPrimitive(t, r) {
  if ("object" != _typeof(t) || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r);
    if ("object" != _typeof(i)) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}

function toPropertyKey(t) {
  var i = toPrimitive(t, "string");
  return "symbol" == _typeof(i) ? i : i + "";
}

function _defineProperty(e, r, t) {
  return (r = toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
    value: t,
    enumerable: true,
    configurable: true,
    writable: true
  }) : e[r] = t, e;
}

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".styles_modalContent__Ii25l.styles_modalContent__Ii25l:focus, .styles_modalContent__Ii25l.styles_modalContent__Ii25l:focus-visible {\n  outline: none;\n}\n@media (min-width: 500px) {\n  .styles_modalContent__Ii25l.styles_modalContent__Ii25l {\n    overflow: visible;\n  }\n}\n\n.styles_content__AOmZC {\n  display: flex;\n  flex-direction: column;\n  gap: 24px;\n  text-align: left;\n}\n@media (max-width: 499px) {\n  .styles_content__AOmZC {\n    margin-top: 24px;\n  }\n}\n\n.styles_inputs__4Rd7i {\n  display: flex;\n  flex-direction: column;\n  gap: 8px;\n}\n.styles_inputs__4Rd7i.styles_inputs__4Rd7i input {\n  border-radius: 8px;\n}\n\n.styles_iconFieldset__xeD0- {\n  border: 0;\n  margin: 0;\n  padding: 0;\n  min-width: 0;\n}\n\n.styles_legend__eOt2m {\n  padding: 0;\n  margin-bottom: 12px;\n  font-weight: var(--font-weight-medium, 500);\n  color: var(--color-text-default, #000);\n}\n\n.styles_srOnly__96Ozw:not(:focus, :active) {\n  clip: rect(0 0 0 0);\n  clip-path: inset(50%);\n  height: 1px;\n  overflow: hidden;\n  position: absolute;\n  white-space: nowrap;\n  width: 1px;\n}\n\n.styles_iconTable__39qaD {\n  display: flex;\n}\n\n.styles_iconButton__tZmXO {\n  flex: 1;\n  height: 50px;\n  margin: 0;\n  padding: 0;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  cursor: pointer;\n  background: var(--color-surface-default, #fff);\n  color: var(--color-icon-accent, #0074bf);\n  border: solid 1px var(--color-border-weak, #ddd);\n  border-left: none;\n  transition: var(--touchable-background-color-transition, background-color 0.2s ease);\n}\n.styles_iconButton__tZmXO:first-child {\n  border-left: solid 1px var(--color-border-weak, #ddd);\n  border-top-left-radius: var(--radius-s, 4px);\n  border-bottom-left-radius: var(--radius-s, 4px);\n}\n.styles_iconButton__tZmXO:last-child {\n  border-top-right-radius: var(--radius-s, 4px);\n  border-bottom-right-radius: var(--radius-s, 4px);\n}\n.styles_iconButton__tZmXO:hover, .styles_iconButton__tZmXO:focus-visible, .styles_iconButton__tZmXO.styles_selected__1sVY1 {\n  background: var(--color-surface-invert, #000);\n  color: var(--color-text-invert, #fff);\n}\n.styles_iconButton__tZmXO.styles_place__P6Ks- svg {\n  width: 24px;\n  height: 24px;\n}\n.styles_iconButton__tZmXO.styles_work__XL3SA svg {\n  width: 20px;\n  height: 20px;\n}\n.styles_iconButton__tZmXO.styles_home__xYJbW svg, .styles_iconButton__tZmXO.styles_school__K3-Yy svg, .styles_iconButton__tZmXO.styles_sport__i0xXI svg, .styles_iconButton__tZmXO.styles_shopping__eQ3br svg {\n  width: 18px;\n  height: 18px;\n}\n\n.styles_buttons__j7XL9 {\n  display: flex;\n  flex-direction: column;\n  gap: 8px;\n}";
var styles = {"modalContent":"styles_modalContent__Ii25l","content":"styles_content__AOmZC","inputs":"styles_inputs__4Rd7i","iconFieldset":"styles_iconFieldset__xeD0-","legend":"styles_legend__eOt2m","srOnly":"styles_srOnly__96Ozw","iconTable":"styles_iconTable__39qaD","iconButton":"styles_iconButton__tZmXO","selected":"styles_selected__1sVY1","place":"styles_place__P6Ks-","work":"styles_work__XL3SA","home":"styles_home__xYJbW","school":"styles_school__K3-Yy","sport":"styles_sport__i0xXI","shopping":"styles_shopping__eQ3br","buttons":"styles_buttons__j7XL9"};
styleInject(css_248z);

const FAVOURITE_ICON_NAMES = ['place', 'home', 'work', 'sport', 'school', 'shopping'];
const toFavouriteIconId = name => `icon-icon_${name}`;

/** Icon picker: one toggle button per favourite icon. */
function FavouriteIconTable({
  selectedIconId = '',
  onSelect,
  lang
}) {
  const {
    t
  } = useTranslation('translation', {
    i18n
  });
  return /*#__PURE__*/jsx("div", {
    className: styles.iconTable,
    children: FAVOURITE_ICON_NAMES.map(name => {
      const selected = selectedIconId === toFavouriteIconId(name);
      return /*#__PURE__*/jsx("button", {
        type: "button",
        className: cx(styles.iconButton, styles[name], selected && styles.selected),
        "aria-pressed": selected,
        "aria-label": t(name, {
          lng: lang
        }),
        onClick: () => onSelect(name),
        children: /*#__PURE__*/jsx(Icon, {
          img: name,
          color: "currentColor"
        })
      }, name);
    })
  });
}
process.env.NODE_ENV !== "production" ? FavouriteIconTable.propTypes = {
  onSelect: PropTypes.func.isRequired,
  lang: PropTypes.string.isRequired,
  selectedIconId: PropTypes.string
} : void 0;

const _excluded = ["defaultName"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), true).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const NO_EDITS = {};

/**
 * Form body of the modal. It lives inside the dialog content, so the user's
 * edits are discarded when the dialog closes.
 */
function FavouriteForm({
  favourite,
  isEdit,
  lang,
  autosuggestComponent,
  saveFavourite,
  cancelSelected,
  handleClose
}) {
  const {
    t
  } = useTranslation('translation', {
    i18n
  });
  const [edits, setEdits] = useState(NO_EDITS);
  const current = _objectSpread(_objectSpread({}, favourite), edits);
  const canSave = !!current.selectedIconId && Number.isFinite(current.lat) && Number.isFinite(current.lon);
  const specifyName = event => {
    const {
      value
    } = event.target;
    setEdits(previous => _objectSpread(_objectSpread({}, previous), {}, {
      name: value
    }));
  };
  const selectIcon = iconName => {
    setEdits(previous => _objectSpread(_objectSpread({}, previous), {}, {
      selectedIconId: toFavouriteIconId(iconName)
    }));
  };
  const save = () => {
    if (!canSave) {
      return;
    }
    const {
        defaultName
      } = current,
      rest = _objectWithoutProperties(current, _excluded);
    saveFavourite(_objectSpread(_objectSpread({}, rest), {}, {
      name: current.name || defaultName,
      type: 'place'
    }));
    if (isEdit) {
      cancelSelected();
    } else {
      handleClose();
    }
  };
  const namePlaceholder = t('input-placeholder', {
    lng: lang
  });
  return /*#__PURE__*/jsxs("div", {
    className: styles.content,
    children: [/*#__PURE__*/jsxs("div", {
      className: styles.inputs,
      children: [/*#__PURE__*/jsx("div", {
        children: autosuggestComponent
      }), /*#__PURE__*/jsx(TextInput, {
        label: namePlaceholder,
        hideLabel: true,
        placeholder: namePlaceholder,
        value: current.name || '',
        onChange: specifyName
      })]
    }), /*#__PURE__*/jsxs("fieldset", {
      className: styles.iconFieldset,
      children: [/*#__PURE__*/jsxs("legend", {
        className: styles.legend,
        children: [t('choose-icon', {
          lng: lang
        }), /*#__PURE__*/jsx("span", {
          className: styles.srOnly,
          children: t('required-text', {
            lng: lang
          })
        })]
      }), /*#__PURE__*/jsx(FavouriteIconTable, {
        selectedIconId: current.selectedIconId,
        onSelect: selectIcon,
        lang: lang
      })]
    }), /*#__PURE__*/jsxs("div", {
      className: styles.buttons,
      children: [/*#__PURE__*/jsx(Button, {
        variant: "primary",
        size: "l",
        expand: true,
        disabled: !canSave,
        "aria-label": canSave ? t('save-place', {
          lng: lang
        }) : t('cannot-save-place', {
          lng: lang
        }),
        onClick: save,
        children: t('save', {
          lng: lang
        })
      }), /*#__PURE__*/jsx(Button, {
        variant: "secondary",
        size: "l",
        expand: true,
        onClick: isEdit ? cancelSelected : handleClose,
        children: t('cancel', {
          lng: lang
        })
      })]
    })]
  });
}
process.env.NODE_ENV !== "production" ? FavouriteForm.propTypes = {
  isEdit: PropTypes.bool.isRequired,
  lang: PropTypes.string.isRequired,
  saveFavourite: PropTypes.func.isRequired,
  cancelSelected: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  autosuggestComponent: PropTypes.node.isRequired,
  favourite: PropTypes.shape({
    name: PropTypes.string,
    defaultName: PropTypes.string,
    selectedIconId: PropTypes.string,
    lat: PropTypes.number,
    lon: PropTypes.number
  })
} : void 0;

const noop = () => {};

/**
 * A dialog for adding a new favourite place or editing an existing one,
 * built on the HSL design system's Modal. Colors, typography and the
 * mobile/desktop layout are handled by the design system, so the dialog
 * follows the active deployment's theme.
 *
 * @example
 * <FavouriteModal
 *   isModalOpen={modalOpen}
 *   handleClose={handleClose}
 *   saveFavourite={onSaveFavourite}
 *   favourite={favourite}
 *   lang={lang}
 *   autosuggestComponent={
 *     <AutoSuggest
 *       sources={['History', 'Datasource']}
 *       targets={['Locations', 'CurrentPosition']}
 *       id="favourite"
 *       autoFocus={false}
 *       placeholder="search-address-or-place"
 *       value={favourite.address || ''}
 *       selectHandler={setLocationProperties}
 *       lang={lang}
 *     />
 *   }
 * />
 */
function FavouriteModal({
  isModalOpen,
  handleClose,
  saveFavourite,
  cancelSelected = noop,
  autosuggestComponent,
  favourite = null,
  lang = 'fi'
}) {
  const {
    t
  } = useTranslation('translation', {
    i18n
  });
  const isEdit = favourite?.favouriteId !== undefined;
  return /*#__PURE__*/jsx(Modal, {
    open: isModalOpen,
    onOpenChange: open => {
      if (!open) {
        handleClose();
      }
    },
    children: /*#__PURE__*/jsx(ModalContent, {
      lang: lang,
      title: t(isEdit ? 'edit-place' : 'save-place', {
        lng: lang
      }),
      className: styles.modalContent,
      "aria-describedby": undefined,
      children: /*#__PURE__*/jsx(FavouriteForm, {
        favourite: favourite,
        isEdit: isEdit,
        lang: lang,
        autosuggestComponent: autosuggestComponent,
        saveFavourite: saveFavourite,
        cancelSelected: cancelSelected,
        handleClose: handleClose
      })
    })
  });
}
process.env.NODE_ENV !== "production" ? FavouriteModal.propTypes = {
  /** Required. Whether the dialog is open.
   * @type {boolean} */
  isModalOpen: PropTypes.bool.isRequired,
  /** Required. Called when the dialog is closed without saving.
   * @type {function} */
  handleClose: PropTypes.func.isRequired,
  /** Required. Called with the favourite when the user saves it.
   * @type {function} */
  saveFavourite: PropTypes.func.isRequired,
  /** Optional, but needed when editing (favourite has a favouriteId): called
   * after saving and when the user presses Cancel, typically to return to the
   * favourites list. Not used when adding a new favourite.
   * @type {function} */
  cancelSelected: PropTypes.func,
  /** Autosuggest component for searching the location of the favourite.
   * @type {node} */
  autosuggestComponent: PropTypes.node.isRequired,
  /** Optional. Object to prefill the dialog. Having a favouriteId puts the
   * dialog in edit mode.
   * @type {object}
   * @property {string} type
   * @property {string} address
   * @property {string} gtfsId
   * @property {string} gid
   * @property {number} lat
   * @property {number} lon
   * @property {string} name
   * @property {string} selectedIconId
   * @property {string} favouriteId
   * @property {string} layer
   * @property {string} defaultName
   */
  favourite: PropTypes.shape({
    type: PropTypes.string,
    address: PropTypes.string,
    gtfsId: PropTypes.string,
    gid: PropTypes.string,
    lat: PropTypes.number,
    lon: PropTypes.number,
    name: PropTypes.string,
    selectedIconId: PropTypes.string,
    favouriteId: PropTypes.string,
    layer: PropTypes.string,
    defaultName: PropTypes.string
  }),
  /** Optional. Language, fi, en or sv. Defaults to fi.
   * @type {string} */
  lang: PropTypes.string
} : void 0;

export { FavouriteModal as default };
//# sourceMappingURL=index.js.map
